// The live enable/disable switch on a configured alarm row (UI-3). Lifted out
// of the pre-redesign configured band (since retired) so there is exactly ONE
// implementation of this control rather than two that drift apart.
//
// Behaviour is unchanged, and three parts of it are load-bearing:
//
//   - NO optimistic UI. The boat is authoritative: the button goes busy, the
//     PATCH lands, and `onChange()` re-reads the snapshot. Flipping locally
//     would let a sounding alarm read as Disabled the moment someone clicked.
//   - The write committing in the CLOUD is not the boat running it, so the
//     response's `delivery.state` is surfaced per action (`describeDelivery`).
//     Silence there is what previously hid "saved, but your boat hasn't got it".
//   - The button keeps its state WORD inside it ("Enabled" / "Disabled" /
//     "Saving…"). The redesign's 46×26 switch has no room to print it, so it
//     rides an `.sr-only` span: it is still the control's accessible name, and
//     still the text the browser tests assert on.
//
// PATCHing `{ enabled }` alone is deliberately recipe-agnostic — the API accepts
// it on custom and recipe definitions alike, because every custom-specific check
// there is gated on the field it validates.

import { useState } from 'react';

import { describeDelivery, type ConfiguredAlarmView } from '../../lib/alarmDisplay';
import { setAlarmEnabled } from '../../lib/alarmApi';
import { isRecipeWithdrawnError } from '../../lib/customAlarmErrors';

const TOGGLE_RETRY = 'Couldn’t save — try again.';
// The cell is narrow, so the short form; the row's Edit panel gives the reason.
// Also the locked switch's tooltip, so the disabled state explains itself.
const TOGGLE_WITHDRAWN = 'Withdrawn — it can’t be switched back on.';

export function EnabledToggle({
  boatId,
  view,
  withdrawn = false,
  onChange,
}: {
  boatId: string;
  view: ConfiguredAlarmView;
  /**
   * The alarm's recipe is WITHDRAWN (TOM-136). Turning it off still works;
   * once off, the switch is disabled — switching it back on can only be refused.
   */
  withdrawn?: boolean;
  /** Re-read the authoritative snapshot once the write lands. */
  onChange: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deliveryNote, setDeliveryNote] = useState<string | null>(null);

  const locked = withdrawn && !view.enabled;

  async function toggle(): Promise<void> {
    if (busy || locked) return;
    setBusy(true);
    setError(null);
    setDeliveryNote(null);
    try {
      const delivery = await setAlarmEnabled(boatId, view.id, !view.enabled);
      // The write committing in the cloud is NOT the same as the boat running
      // it. Say so when they differ — silence here is what let a sounding alarm
      // read as Disabled.
      setDeliveryNote(describeDelivery(delivery));
      // Boat stays authoritative: re-read the snapshot rather than flip locally.
      onChange();
    } catch (err) {
      // Switching on an alarm whose recipe is withdrawn is refused (TOM-136),
      // and retrying cannot help, so it says so. Anything else: the retry line.
      setError(isRecipeWithdrawnError(err) ? TOGGLE_WITHDRAWN : TOGGLE_RETRY);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="alarm-row__toggle al-toggle-cell">
      <button
        type="button"
        className={`al-toggle${view.enabled ? ' al-toggle--on' : ''}`}
        role="switch"
        aria-checked={view.enabled}
        disabled={busy || locked}
        title={locked ? TOGGLE_WITHDRAWN : undefined}
        data-testid={`alarm-toggle-${view.id}`}
        onClick={() => void toggle()}
      >
        <span className="al-toggle__knob" aria-hidden="true" />
        <span className="sr-only">{busy ? 'Saving…' : view.enabled ? 'Enabled' : 'Disabled'}</span>
      </button>
      {error !== null ? (
        <span className="alarm-toggle__err" data-testid={`alarm-toggle-err-${view.id}`}>
          {error}
        </span>
      ) : null}
      {deliveryNote !== null ? (
        <span className="alarm-toggle__note" data-testid={`alarm-toggle-note-${view.id}`}>
          {deliveryNote}
        </span>
      ) : null}
    </div>
  );
}
